<template>
  <div class="landing-page">
    <div class="container">
      <!-- Logo -->
      <div class="logo-container">
        <img
          src="/logo.png"
          alt="Hrafnabók Logo"
          class="logo"
        />
      </div>

      <!-- App Name -->
      <h1 class="app-title">Hrafnabók</h1>

      <!-- Subtitle -->
      <p class="subtitle">Unlock Icelandic</p>

      <!-- Waitlist Form -->
      <div class="waitlist-section">
        <h2 class="waitlist-title">Join the Waiting List</h2>
        <p class="waitlist-description">Be the first to know when Hrafnabók launches!</p>
        
        <form @submit.prevent="submitEmail" class="waitlist-form">
          <div class="form-group">
            <input 
              v-model="email"
              type="email" 
              placeholder="Enter your email address"
              required 
              class="email-input"
              :disabled="isSubmitting"
            />
            <button 
              type="submit" 
              class="submit-btn"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Joining...' : 'Join Waiting List' }}
            </button>
          </div>
        </form>
        
        <div v-if="message" :class="['message', messageType]">
          {{ message }}
        </div>
      </div>

      <!-- Store Buttons -->
      <div class="buttons-container">
        <button class="store-button" disabled>
          <span class="button-content">
            <span class="icon-container">
              <img src="../assets/appstore.svg" alt="App Store" class="store-icon" />
            </span>
            <span class="button-text">
              <span class="coming-soon">Coming Soon</span>
              <span class="store-name">App Store</span>
            </span>
          </span>
        </button>

        <button class="store-button" disabled>
          <span class="button-content">
            <span class="icon-container">
              <img src="../assets/playstore.svg" alt="Google Play" class="store-icon" />
            </span>
            <span class="button-text">
              <span class="coming-soon">Coming Soon</span>
              <span class="store-name">Google Play</span>
            </span>
          </span>
        </button>
      </div>

      <!-- Footer Links -->
      <div class="footer-links">
        <router-link to="/terms" class="footer-link">Terms of Service</router-link>
        <span class="separator">•</span>
        <router-link to="/privacy" class="footer-link">Privacy Policy</router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  data() {
    return {
      email: '',
      isSubmitting: false,
      message: '',
      messageType: ''
    }
  },
  methods: {
    async submitEmail() {
      this.isSubmitting = true;
      this.message = '';
      
      try {
        const response = await fetch('https://cloudflare-waiting-list.brotunity.workers.dev/api/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.email })
        });
        
        const result = await response.json();
        
        if (result.success) {
          this.message = result.message;
          this.messageType = 'success';
          this.email = '';
        } else {
          this.message = result.error;
          this.messageType = 'error';
        }
        
      } catch (error) {
        this.message = 'Network error. Please try again.';
        this.messageType = 'error';
      }
      
      this.isSubmitting = false;
    }
  }
}
</script>

<style scoped>
.landing-page {
  min-height: 100vh;
  background: white;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
}

@media (min-width: 768px) {
  .landing-page {
    padding-top: 0;
  }
}

.container {
  max-width: 64rem;
  margin: 0 auto;
  text-align: center;
}

.logo-container {
  margin-bottom: 3rem;
}

.logo {
  width: 12rem;
  height: 12rem;
  margin: 0 auto;
  filter: drop-shadow(0 25px 50px rgba(0, 0, 0, 0.25)) drop-shadow(0 0 30px rgba(69, 198, 158, 0.4));
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

@media (min-width: 768px) {
  .logo {
    width: 36rem;
    height: 36rem;
  }
}

@media (min-width: 1024px) {
  .logo {
    width: 48rem;
    height: 48rem;
  }
}

.app-title {
  font-size: 3.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, hsl(173, 58%, 39%), hsl(173, 58%, 50%));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
}

@media (min-width: 768px) {
  .app-title {
    font-size: 5rem;
  }
}

.subtitle {
  font-size: 1.25rem;
  color: black;
  margin-bottom: 3rem;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

@media (min-width: 768px) {
  .subtitle {
    font-size: 1.875rem;
  }
}

.waitlist-section {
  margin-bottom: 4rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
}

.waitlist-title {
  font-size: 1.875rem;
  font-weight: 600;
  color: black;
  margin-bottom: 0.75rem;
}

@media (min-width: 768px) {
  .waitlist-title {
    font-size: 2.25rem;
  }
}

.waitlist-description {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.5;
}

@media (min-width: 768px) {
  .waitlist-description {
    font-size: 1.125rem;
  }
}

.waitlist-form {
  margin-bottom: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 640px) {
  .form-group {
    flex-direction: row;
  }
}

.email-input {
  flex: 1;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
}

.email-input:focus {
  outline: none;
  border-color: hsl(173, 58%, 39%);
  box-shadow: 0 0 0 3px hsla(173, 58%, 39%, 0.1);
}

.email-input:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.submit-btn {
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, hsl(173, 58%, 39%), hsl(173, 58%, 50%));
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, hsl(173, 58%, 35%), hsl(173, 58%, 45%));
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(69, 198, 158, 0.3);
}

.submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.message {
  padding: 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
}

.message.success {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 3rem;
}

@media (min-width: 640px) {
  .buttons-container {
    flex-direction: row;
  }
}

.store-button {
  width: 240px;
  height: 64px;
  background: white;
  border: 2px solid black;
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: not-allowed;
  opacity: 0.8;
}

.store-button:hover {
  background: #f9fafb;
  border-color: #374151;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.button-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
  height: 100%;
}

.icon-container {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-icon {
  width: 48px;
  height: 48px;
}

.button-text {
  text-align: left;
  color: black;
  display: flex;
  flex-direction: column;
}

.coming-soon {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.store-name {
  font-size: 1.125rem;
  font-weight: 600;
}

.footer-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
  font-size: 0.875rem;
}

.footer-link {
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: hsl(173, 58%, 39%);
}

.separator {
  color: #d1d5db;
}
</style>